import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './Login.module.css';
import { useLang } from '../i18n/LanguageContext';
import { resetPasswordText } from '../i18n/pages/resetPassword';

const BookIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { lang } = useLang();
  const t = resetPasswordText[lang];

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginContainer}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}><BookIcon /></div>
            <span className={styles.logoText}>Guestbook<span>.cz</span></span>
          </div>
          <div className={styles.title}>{t.invalidLinkTitle}</div>
          <div className={styles.subtitle}>{t.invalidLinkSubtitle}</div>
          <div className={styles.footer} style={{ marginTop: 24 }}>
            <Link to="/forgot-password">{t.requestNewLink}</Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError(t.errorMismatch); return; }
    if (password.length < 8) { setError(t.errorTooShort); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Reset failed');
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginContainer}>
        <div className={styles.logo}>
          <div className={styles.logoIcon}><BookIcon /></div>
          <span className={styles.logoText}>Guestbook<span>.cz</span></span>
        </div>

        <div className={styles.title}>{t.title}</div>
        <div className={styles.subtitle}>{done ? t.subtitleDone : t.subtitleDefault}</div>

        {done ? (
          <>
            <p style={{ textAlign: 'center', color: '#334155', fontSize: '0.9rem', lineHeight: 1.6, margin: '8px 0 24px' }}>
              {t.doneBody}
            </p>
            <div className={styles.footer}>
              <Link to="/login">{t.signIn}</Link>
            </div>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t.newPasswordLabel}</label>
                <input className={styles.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t.newPasswordPlaceholder} required autoFocus />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t.confirmPasswordLabel}</label>
                <input className={styles.input} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t.confirmPasswordPlaceholder} required />
              </div>
              <button className={styles.button} type="submit" disabled={loading}>
                {loading ? t.submittingBtn : t.submitBtn}
              </button>
            </form>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.footer}>
              <Link to="/login">{t.backToLogin}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
